import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getSpaCategories, saveSpaCategory, exportSpaCategoriesToExcel } from "../../components/Api/GmsSpaCategory";
import { SquarePen, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

const defaultForm = {
  category_Id: 0,
  name_En: "",
  name_Ar: "",
  status: true,
  modifiedBy: 1,
  modifiedOn: new Date().toISOString(),
};

const emptyFilter = {
  name_En: "",
  name_Ar: "",
  status: null,
};

export default function SpaCategoryPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaCategory');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSpaCategory');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSpaCategory');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsSpaCategory');
  const isAdmin = isUserAdmin();

  const [spaCategories, setSpaCategories] = useState([]);
  const [form, setForm] = useState(() => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.status : true;
    return { ...defaultForm, status: defaultStatus };
  });
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      return dateTimeStr.split('T')[0];
    } catch (error) {
      return "N/A";
    }
  };

  const formatFullDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  useEffect(() => {
    console.log('Component mounted');
    if (hasPermission) {
      console.log('Loading data due to permission');
      loadSpaCategories(); // Load spa categories on component mount
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      console.log('No permission to view page');
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadSpaCategories();
    }
  }, [hasAppliedFilter]);

  const loadSpaCategories = async (filterData = filter) => {
    setLoading(true);
    try {
      const data = await getSpaCategories(filterData);
      setSpaCategories(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load spa categories: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // If status is changed, update the filter immediately
    if (field === 'status') {
      setHasAppliedFilter(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name_En) {
      Swal.fire("Error", "Please fill in Category Name (English)", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const session = getUserSession();
      const categoryData = {
        ...form,
        modifiedBy: session.userId,
        modifiedOn: new Date().toISOString(),
      };

      const result = await saveSpaCategory(categoryData);
      
      if (result.id) {
        Swal.fire("Success", "Spa Category saved successfully!", "success");
        handleClear();
        if (hasAppliedFilter) {
          loadSpaCategories();
        }
      }
    } catch (error) {
      let errorMessage = "Failed to save spa category";
      
      // Extract error message from API response
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          // Extract just the error message from the exception string
          const match = error.response.data.match(/System\.InvalidOperationException:\s*(.+?)(?:\s+at\s+|$)/);
          errorMessage = match ? match[1].trim() : error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditLoadingRow(category.category_Id);
    try {
      setForm({
        category_Id: category.category_Id,
        name_En: category.name_En || "",
        name_Ar: category.name_Ar || "",
        status: category.status,
        modifiedBy: category.modifiedBy,
        modifiedOn: category.modifiedOn,
      });
      setIsEditing(true);
      setActiveTab("1"); // Switch to form tab
    } catch (error) {
      Swal.fire("Error", "Failed to load category data", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleClear = () => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.status : true;
    setForm({ ...defaultForm, status: defaultStatus });
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
    setPage(1);
    loadSpaCategories(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(false);
    setSpaCategories([]);
    setTotalCount(0);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.name_En && filter.name_En.trim() !== '' && { name_En: filter.name_En }),
        ...(filter.name_Ar && filter.name_Ar.trim() !== '' && { name_Ar: filter.name_Ar }),
        ...(filter.status !== null && filter.status !== undefined && { status: filter.status })
      };
      
      await exportSpaCategoriesToExcel(cleanFilter);
      Swal.fire("Success", "Spa category report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting spa categories:", error);
      Swal.fire("Error", "Failed to export spa category report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getFilteredSpaCategories = () => {
    if (!hasAppliedFilter) return spaCategories;
    
    return spaCategories.filter(category => {
      const nameEnMatch = !filter.name_En || 
        category.name_En.toLowerCase().includes(filter.name_En.toLowerCase());
      const nameArMatch = !filter.name_Ar || 
        category.name_Ar.toLowerCase().includes(filter.name_Ar.toLowerCase());
      const statusMatch = filter.status === null || category.status === filter.status;
      return nameEnMatch && nameArMatch && statusMatch;
    });
  };

  const getPaginatedSpaCategories = () => {
    const filteredCategories = getFilteredSpaCategories();
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredCategories.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(totalCount / pageSize);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      // Refresh categories when switching to Category List tab
      if (tab === "2") {
        loadSpaCategories();
      }
    }
  };

  const getCategoryNameOptions = () => {
    return spaCategories.map(category => ({
      value: category.name_En,
      label: category.name_En
    }));
  };

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Spa Category Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => toggle("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Category Form</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => toggle("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Category List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="name_En">Category Name (English) <span className="text-danger">*</span></Label>
                              <Input
                                type="text"
                                id="name_En"
                                value={form.name_En}
                                onChange={(e) => handleFormChange("name_En", e.target.value)}
                                placeholder="Enter category name in English"
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="name_Ar">Category Name (Arabic)</Label>
                              <Input
                                type="text"
                                id="name_Ar"
                                value={form.name_Ar}
                                onChange={(e) => handleFormChange("name_Ar", e.target.value)}
                                placeholder="Enter category name in Arabic"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        {hasDeletePermission && (
                          <Row>
                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <div className="form-check form-switch">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="status"
                                    checked={form.status}
                                    onChange={(e) => handleFormChange("status", e.target.checked)}
                                  />
                                  <label className="form-check-label" htmlFor="status">
                                    {form.status ? "Active" : "Inactive"}
                                  </label>
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        )}
                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isEditing ? "Update Category" : "Save Category"}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </TabPane>
                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Categories</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={4}>
                                <Label for="filterNameEn">Category Name (English)</Label>
                                <Select
                                  id="filterNameEn"
                                  value={getCategoryNameOptions().find(option => option.value === filter.name_En)}
                                  onChange={(selectedOption) => 
                                    handleFilterChange("name_En", selectedOption ? selectedOption.value : "")
                                  }
                                  options={getCategoryNameOptions()}
                                  isClearable
                                  isSearchable
                                  placeholder="Search category name..."
                                  className="react-select"
                                  classNamePrefix="react-select"
                                />
                              </Col>
                              <Col md={4}>
                                <Label for="filterNameAr">Category Name (Arabic)</Label>
                                <Input
                                  type="text"
                                  id="filterNameAr"
                                  value={filter.name_Ar}
                                  onChange={(e) => handleFilterChange("name_Ar", e.target.value)}
                                  placeholder="Search category name in Arabic"
                                />
                              </Col>
                              <Col md={4}>
                                <Label for="filterStatus">Status</Label>
                                <Select
                                  id="filterStatus"
                                  value={statusOptions.find(option => option.value === filter.status)}
                                  onChange={(selectedOption) =>
                                    handleFilterChange("status", selectedOption ? selectedOption.value : null)
                                  }
                                  options={statusOptions}
                                  isClearable
                                  placeholder="Select Status"
                                  className="react-select"
                                  classNamePrefix="react-select"
                                />
                              </Col>
                              <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    onClick={handleApplyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={handleClearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Category List
                            </h5>
                            <div className="d-flex gap-2">
                              <Button
                                color="success"
                                onClick={handleExportExcel}
                                disabled={loading || totalCount === 0}
                                className="waves-effect waves-light"
                              >
                                <Download className="me-1" size={16} />
                                Export Excel
                              </Button>
                              {hasEditOrHigher && (
                                <Button
                                  color="primary"
                                  onClick={() => toggle("1")}
                                  className="waves-effect waves-light"
                                >
                                  <i className="bx bx-plus me-1"></i>
                                  Add New Category
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading categories...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="5%">#</th>
                                      <th width="25%">Category Name (En)</th>
                                      <th width="25%">Category Name (Ar)</th>
                                      <th width="15%">Status</th>
                                      <th width="20%">Modified Date</th>
                                      <th width="10%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getPaginatedSpaCategories().length > 0 ? (
                                      getPaginatedSpaCategories().map((category, index) => (
                                        <tr key={category.category_Id}>
                                          <td>{(page - 1) * pageSize + index + 1}</td>
                                          <td>{category.name_En}</td>
                                          <td>{category.name_Ar || "N/A"}</td>
                                          <td>
                                            <span className={`badge ${category.status ? 'bg-success' : 'bg-danger'}`}>
                                              {category.status ? "Active" : "Inactive"}
                                            </span>
                                          </td>
                                          <td>{formatDateTime(category.modifiedOn)}</td>
                                          <td>
                                            {hasEditOrHigher && (
                                              <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => handleEdit(category)}
                                                disabled={editLoadingRow === category.category_Id}
                                                className="waves-effect waves-light"
                                              >
                                                {editLoadingRow === category.category_Id ? (
                                                  <ClipLoader size={12} color="#ffffff" />
                                                ) : (
                                                  <SquarePen size={12} />
                                                )}
                                              </Button>
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="6" className="text-center">
                                          {hasAppliedFilter ? "No categories found matching the filter criteria." : "No categories available. Please add some categories first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              {getTotalPages() > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Previous
                                    </Button>
                                    <span className="align-self-center mx-2">
                                      Page {page} of {getTotalPages()}
                                    </span>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page + 1)}
                                      disabled={page === getTotalPages()}
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
